import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '@/lib/api/client';

export const analyticsKeys = {
  all: ['analytics'] as const,
  summary: (params?: { start?: string; end?: string }) => [...analyticsKeys.all, 'summary', params] as const,
  byStrategy: () => [...analyticsKeys.all, 'by-strategy'] as const,
  pnlHistory: (period?: string) => [...analyticsKeys.all, 'pnl-history', period] as const,
  premiumFlow: (params?: { period?: string; symbol?: string }) => [...analyticsKeys.all, 'premium-flow', params] as const,
  symbols: () => [...analyticsKeys.all, 'symbols'] as const,
  capital: () => [...analyticsKeys.all, 'capital'] as const,
};

/**
 * Capital held per chain and per expiration, beside Schwab's balances. Held moves only when a
 * position does, and the Schwab half is KV-cached for 60s server-side, so a matching client
 * staleTime keeps a page switch from re-fetching for nothing.
 */
export function useCapital() {
  return useQuery({
    queryKey: analyticsKeys.capital(),
    queryFn: () => analyticsApi.getCapital(),
    staleTime: 60_000,
  });
}

export function usePerformanceSummary(params?: { start?: string; end?: string }) {
  return useQuery({
    queryKey: analyticsKeys.summary(params),
    queryFn: () => analyticsApi.getSummary(params),
    select: (data) => data.summary,
  });
}

export function useStrategyPerformance() {
  return useQuery({
    queryKey: analyticsKeys.byStrategy(),
    queryFn: () => analyticsApi.getByStrategy(),
    select: (data) => data.strategies,
  });
}

export function usePnlHistory(period?: string) {
  return useQuery({
    queryKey: analyticsKeys.pnlHistory(period),
    queryFn: () => analyticsApi.getPnlHistory(period),
    select: (data) => data.dataPoints,
  });
}

export function usePremiumFlow(params?: { period?: string; symbol?: string }) {
  return useQuery({
    queryKey: analyticsKeys.premiumFlow(params),
    queryFn: () => analyticsApi.getPremiumFlow(params),
    select: (data) => data.flow,
  });
}

export function useSymbolPerformance() {
  return useQuery({
    queryKey: analyticsKeys.symbols(),
    queryFn: () => analyticsApi.getSymbols(),
    select: (data) => data.symbols,
  });
}
